import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { uploadDishImage, uploadARModel } from '../../../services/storageService';
import DishScannerModal from './DishScannerModal';

const DishEditorRow = ({ dish, isSelected, onSelect, onUpdate, onDelete, categories, restaurantId }) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [imagePreview, setImagePreview] = useState(dish?.image);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [dimErrors, setDimErrors] = useState({});
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [scanPhotos, setScanPhotos] = useState(dish?.scanPhotos || null);
  const [isGlbPreviewOpen, setIsGlbPreviewOpen] = useState(false);
  const [glbDiagnostics, setGlbDiagnostics] = useState(null);

  const CHIP_OPTIONS = [
    { value: 'new', label: 'New', className: 'bg-blue-500 text-white border-blue-500' },
    { value: 'veg', label: 'Veg', className: 'bg-green-500 text-white border-green-500' },
    { value: 'spicy', label: 'Spicy', className: 'bg-red-500 text-white border-red-500' },
    { value: 'popular', label: 'Popular', className: 'bg-amber-500 text-white border-amber-500' },
  ];

  const imageInputRef = useRef(null);
  const arInputRef = useRef(null);

  const DIM_MAX = 9999;
  const DIM_MIN = 0.001;

  const validateDimension = (field, value) => {
    if (value === null || value === undefined || value === '') return null;
    const num = parseFloat(value);
    if (isNaN(num)) return 'Valeur invalide';
    if (num === 0) return 'La valeur ne peut pas être zéro';
    if (num < 0) return 'La valeur doit être positive';
    if (num < DIM_MIN) return `Min ${DIM_MIN} cm`;
    if (num > DIM_MAX) return `Max ${DIM_MAX} cm — vérifiez la mesure`;
    return null;
  };

  const handleStartEdit = (field) => {
    setIsEditing(field);
    setEditValues({ ...editValues, [field]: dish?.[field] });
    setUploadError('');
  };

  const handleSaveEdit = (field) => {
    const dimFields = ['widthCm', 'heightCm', 'depthCm'];
    if (dimFields?.includes(field)) {
      const err = validateDimension(field, editValues?.[field]);
      if (err) {
        setDimErrors({ ...dimErrors, [field]: err });
        return;
      }
      setDimErrors({ ...dimErrors, [field]: null });
    }
    onUpdate(dish?.id, field, editValues?.[field]);
    if (field === 'image') {
      setImagePreview(editValues?.[field]);
    }
    setIsEditing(null);
  };

  const handleCancelEdit = () => {
    setIsEditing(null);
    setEditValues({});
    setUploadError('');
    setDimErrors({});
  };

  const handleKeyDown = (e, field) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSaveEdit(field);
    } else if (e?.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleImageFileChange = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setIsUploading(true);
    setUploadError('');
    try {
      const url = await uploadDishImage(file, restaurantId);
      setImagePreview(url);
      onUpdate(dish?.id, 'image', url);
      setIsEditing(null);
    } catch (err) {
      setUploadError('Échec du téléversement de l\'image.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleArFileChange = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const MAX_GLB_SIZE = 500 * 1024 * 1024; // 500 MB
    if (file?.size > MAX_GLB_SIZE) {
      setUploadError('Le fichier est trop volumineux. La taille maximale est de 500 Mo.');
      e.target.value = '';
      return;
    }

    // Validate by extension — 3DGS exporters (KIRI Engine, Luma AI, Polycam, etc.)
    // often produce files with non-standard MIME types, so extension is the primary gate.
    const ext = file?.name?.split('.')?.pop()?.toLowerCase();
    // .splat is accepted here so we can run diagnostics and show a helpful message
    const supportedExtensions = ['glb', 'gltf', 'usdz', 'ply', 'splat'];
    if (!supportedExtensions?.includes(ext)) {
      setUploadError('Format non supporté. Veuillez utiliser un fichier .glb ou .gltf exporté depuis KIRI Engine (mode Mesh).');
      e.target.value = '';
      return;
    }

    setIsUploading(true);
    setUploadError('');
    setGlbDiagnostics(null);
    try {
      const result = await uploadARModel(file, restaurantId);
      onUpdate(dish?.id, 'arUrl', result?.url);
      setGlbDiagnostics(result?.diagnostics || null);
      setIsEditing(null);
    } catch (err) {
      // Attach diagnostics to state so the UI can show detailed info
      if (err?.diagnostics) {
        setGlbDiagnostics(err?.diagnostics);
      }

      const diag = err?.diagnostics;
      const mode = diag?.detectedExportMode;

      // Build a user-friendly error message based on detected export mode
      if (mode === 'GAUSSIAN_SPLAT') {
        setUploadError(
          'Fichier .splat non supporté par les visionneuses WebXR. ' + 'Dans KIRI Engine : Export → GLB → choisir le mode "Mesh" (pas "Splat").'
        );
      } else if (diag?.issues?.some(i => i?.includes('truncated') || i?.includes('corrupt'))) {
        setUploadError(
          `Fichier GLB corrompu ou incomplet. ${diag?.issues?.[0]} — Réexportez depuis KIRI Engine.`
        );
      } else if (err?.supabaseError) {
        setUploadError(
          `Échec du téléversement (${err?.supabaseError?.statusCode || 'erreur réseau'}): ${err?.supabaseError?.message}`
        );
      } else {
        setUploadError('Échec du téléversement du modèle AR. Vérifiez que le fichier GLB est valide.');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleScanComplete = (scanData) => {
    setScanPhotos(scanData?.photos);
    onUpdate(dish?.id, 'scanPhotos', scanData?.photos);
    onUpdate(dish?.id, 'scanCapturedAt', scanData?.capturedAt);
  };

  const isHidden = dish?.hidden === true;

  return (
    <>
      <tr className={`border-b border-border transition-smooth ${isHidden ? 'opacity-50 bg-muted/20' : 'hover:bg-muted/30'}`}>
        {/* Image */}
        <td className="px-4 py-3">
          <div className="relative group">
            <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted">
              {isUploading && isEditing === 'image' ? (
                <div className="w-full h-full flex items-center justify-center">
                  <Icon name="Loader" size={20} color="var(--color-primary)" className="animate-spin" />
                </div>
              ) : (
                <Image
                  src={imagePreview}
                  alt={dish?.name}
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            <button
              onClick={() => { handleStartEdit('image'); imageInputRef?.current?.click(); }}
              className="absolute inset-0 bg-background/80 opacity-0 group-hover:opacity-100 transition-smooth flex items-center justify-center rounded-lg"
              title="Changer l'image"
            >
              <Icon name="Upload" size={20} color="var(--color-primary)" />
            </button>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={handleImageFileChange}
            />
          </div>
          {uploadError && isEditing === 'image' && (
            <p className="text-xs text-destructive mt-1 max-w-[80px]">{uploadError}</p>
          )}
        </td>
        {/* Name */}
        <td className="px-4 py-3">
          {isEditing === 'name' ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={editValues?.name || ''}
                onChange={(e) => setEditValues({ ...editValues, name: e?.target?.value })}
                onKeyDown={(e) => handleKeyDown(e, 'name')}
                className="w-full px-2 py-1 border border-input rounded text-sm"
                autoFocus
              />
              <Button onClick={() => handleSaveEdit('name')} size="xs" iconName="Check" />
              <Button onClick={handleCancelEdit} variant="ghost" size="xs" iconName="X" />
            </div>
          ) : (
            <div
              onClick={() => handleStartEdit('name')}
              className="cursor-pointer hover:bg-muted/50 px-2 py-1 rounded transition-smooth min-w-[150px]"
            >
              <p className="text-sm font-medium text-foreground">{dish?.name}</p>
            </div>
          )}
        </td>
        {/* Description */}
        <td className="px-4 py-3">
          {isEditing === 'description' ? (
            <div className="flex items-start gap-2">
              <textarea
                value={editValues?.description || ''}
                onChange={(e) => setEditValues({ ...editValues, description: e?.target?.value })}
                onKeyDown={(e) => handleKeyDown(e, 'description')}
                className="w-full px-2 py-1 border border-input rounded text-sm min-h-[60px]"
                autoFocus
              />
              <div className="flex flex-col gap-1">
                <Button onClick={() => handleSaveEdit('description')} size="xs" iconName="Check" />
                <Button onClick={handleCancelEdit} variant="ghost" size="xs" iconName="X" />
              </div>
            </div>
          ) : (
            <div
              onClick={() => handleStartEdit('description')}
              className="cursor-pointer hover:bg-muted/50 px-2 py-1 rounded transition-smooth min-w-[200px]"
            >
              <p className="text-sm text-muted-foreground line-clamp-2">{dish?.description}</p>
            </div>
          )}
        </td>
        {/* Price */}
        <td className="px-4 py-3">
          {isEditing === 'price' ? (
            <div className="flex items-center gap-2">
              <input
                type="number"
                step="0.01"
                value={editValues?.price || ''}
                onChange={(e) => setEditValues({ ...editValues, price: parseFloat(e?.target?.value) })}
                onKeyDown={(e) => handleKeyDown(e, 'price')}
                className="w-24 px-2 py-1 border border-input rounded text-sm"
                autoFocus
              />
              <Button onClick={() => handleSaveEdit('price')} size="xs" iconName="Check" />
              <Button onClick={handleCancelEdit} variant="ghost" size="xs" iconName="X" />
            </div>
          ) : (
            <div
              onClick={() => handleStartEdit('price')}
              className="cursor-pointer hover:bg-muted/50 px-2 py-1 rounded transition-smooth"
            >
              <p className="text-sm font-semibold text-primary">${dish?.price?.toFixed(2)}</p>
            </div>
          )}
        </td>
        {/* Category */}
        <td className="px-4 py-3">
          {isEditing === 'category' ? (
            <div className="flex items-center gap-2">
              <select
                value={editValues?.category || dish?.category}
                onChange={(e) => setEditValues({ ...editValues, category: e?.target?.value })}
                className="px-2 py-1 border border-input rounded text-sm"
                autoFocus
              >
                {categories?.filter(c => c?.value !== 'all')?.map(cat => (
                  <option key={cat?.value} value={cat?.value}>{cat?.label}</option>
                ))}
              </select>
              <Button onClick={() => handleSaveEdit('category')} size="xs" iconName="Check" />
              <Button onClick={handleCancelEdit} variant="ghost" size="xs" iconName="X" />
            </div>
          ) : (
            <div
              onClick={() => handleStartEdit('category')}
              className="cursor-pointer hover:bg-muted/50 px-2 py-1 rounded transition-smooth"
            >
              <span className="text-sm text-muted-foreground">
                {categories?.find(c => c?.value === dish?.category)?.label}
              </span>
            </div>
          )}
        </td>
        {/* AR URL */}
        <td className="px-4 py-3">
          <div className="flex flex-col gap-1">
            <button
              onClick={() => { handleStartEdit('arUrl'); arInputRef?.current?.click(); }}
              className="flex items-center gap-1.5 px-2 py-1 rounded text-xs text-muted-foreground hover:bg-muted/50 transition-smooth"
              title="Téléverser un fichier AR"
              disabled={isUploading}
            >
              {isUploading && isEditing === 'arUrl' ? (
                <Icon name="Loader" size={14} className="animate-spin" />
              ) : (
                <Icon name="Upload" size={14} />
              )}
              {dish?.arUrl ? (
                <span className="text-primary underline truncate max-w-[120px]" title={dish?.arUrl}>
                  {isUploading && isEditing === 'arUrl' ? 'Téléversement...' : 'Modifier AR'}
                </span>
              ) : (
                <span className="italic">
                  {isUploading && isEditing === 'arUrl' ? 'Téléversement...' : 'Ajouter fichier AR'}
                </span>
              )}
            </button>
            {dish?.arUrl && (
              <>
                <span className="text-xs text-muted-foreground truncate max-w-[160px]" title={dish?.arUrl}>
                  ✓ Fichier AR lié
                </span>
                <button
                  onClick={() => setIsGlbPreviewOpen(true)}
                  className="flex items-center gap-1 px-2 py-1 rounded text-xs text-primary hover:bg-primary/10 transition-smooth border border-primary/30 w-fit"
                  title="Prévisualiser le modèle 3D"
                >
                  <Icon name="Box" size={12} />
                  Prévisualiser
                </button>
              </>
            )}
            {uploadError && isEditing === 'arUrl' && (
              <div className="flex flex-col gap-1">
                <p className="text-xs text-destructive">{uploadError}</p>
                {glbDiagnostics && (
                  <div className="mt-1 p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-800 max-w-[220px]">
                    <p className="font-semibold mb-1 flex items-center gap-1">
                      <Icon name="AlertTriangle" size={11} />
                      Diagnostic GLB
                    </p>
                    <p>Mode détecté : <span className="font-mono">{glbDiagnostics?.detectedExportMode}</span></p>
                    {glbDiagnostics?.jsonContent?.generator && (
                      <p>Générateur : {glbDiagnostics?.jsonContent?.generator}</p>
                    )}
                    {glbDiagnostics?.chunks?.length > 0 && (
                      <p>Chunks : {glbDiagnostics?.chunks?.map(c => c?.chunkType)?.join(', ')}</p>
                    )}
                    {glbDiagnostics?.issues?.length > 0 && (
                      <ul className="mt-1 list-disc list-inside space-y-0.5">
                        {glbDiagnostics?.issues?.map((issue, i) => (
                          <li key={i}>{issue}</li>
                        ))}
                      </ul>
                    )}
                    {['KIRI_3DGS_EMBEDDED', 'KIRI_PHOTOGRAMMETRY_MESH', 'KIRI_UNKNOWN', 'GAUSSIAN_SPLAT']?.includes(glbDiagnostics?.detectedExportMode) && (
                      <p className="mt-1 text-amber-600 font-medium">
                        💡 KIRI Engine : Export → GLB → mode "Mesh"
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            <input
              ref={arInputRef}
              type="file"
              accept=".glb,.gltf,.usdz,.ply,.splat,model/gltf-binary,model/gltf+json,model/vnd.usdz+zip,application/octet-stream,application/x-binary,application/binary"
              className="hidden"
              onChange={handleArFileChange}
            />
          </div>
        </td>
        {/* AR Dimensions */}
        <td className="px-4 py-3">
          <div className="flex flex-col gap-1.5 min-w-[160px]">
            <p className="text-xs font-medium text-muted-foreground mb-0.5">Dimensions AR (cm)</p>
            {['widthCm', 'heightCm', 'depthCm']?.map((field) => {
              const labels = { widthCm: 'L', heightCm: 'H', depthCm: 'P' };
              const currentError = dimErrors?.[field];
              const liveVal = editValues?.[field];
              const liveNum = liveVal !== null && liveVal !== undefined && liveVal !== '' ? parseFloat(liveVal) : null;
              const showRangeWarning = liveNum !== null && !isNaN(liveNum) && liveNum > DIM_MAX;
              return (
                <div key={field} className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground w-4 shrink-0">{labels?.[field]}</span>
                    {isEditing === field ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          step="0.1"
                          min={DIM_MIN}
                          max={DIM_MAX}
                          value={editValues?.[field] ?? ''}
                          onChange={(e) => {
                            const raw = e?.target?.value;
                            const parsed = raw === '' ? null : parseFloat(raw);
                            setEditValues({ ...editValues, [field]: parsed });
                            setDimErrors({ ...dimErrors, [field]: validateDimension(field, parsed) });
                          }}
                          onKeyDown={(e) => handleKeyDown(e, field)}
                          className={`w-16 px-1.5 py-0.5 border rounded text-xs ${currentError ? 'border-destructive focus:ring-destructive' : 'border-input'}`}
                          autoFocus
                        />
                        <Button onClick={() => handleSaveEdit(field)} size="xs" iconName="Check" />
                        <Button onClick={handleCancelEdit} variant="ghost" size="xs" iconName="X" />
                      </div>
                    ) : (
                      <div
                        onClick={() => handleStartEdit(field)}
                        className="cursor-pointer hover:bg-muted/50 px-1.5 py-0.5 rounded transition-smooth flex-1"
                      >
                        <span className="text-xs text-foreground">
                          {dish?.[field] != null ? `${dish?.[field]} cm` : <span className="italic text-muted-foreground">—</span>}
                        </span>
                      </div>
                    )}
                  </div>
                  {isEditing === field && currentError && (
                    <p className="text-xs text-destructive pl-5">{currentError}</p>
                  )}
                  {isEditing === field && showRangeWarning && !currentError && (
                    <p className="text-xs text-amber-600 pl-5">⚠ Valeur inhabituellement grande</p>
                  )}
                </div>
              );
            })}
          </div>
        </td>
        {/* Chip Badge */}
        <td className="px-4 py-3">
          <div className="flex flex-col gap-1.5 min-w-[110px]">
            <p className="text-xs font-medium text-muted-foreground mb-0.5">Badge</p>
            <div className="flex flex-wrap gap-1">
              {CHIP_OPTIONS?.map((chip) => {
                const activeChips = Array.isArray(dish?.chips) ? dish?.chips : (dish?.chip ? [dish?.chip] : []);
                const isActive = activeChips?.includes(chip?.value);
                return (
                  <button
                    key={chip?.value}
                    onClick={() => {
                      const current = Array.isArray(dish?.chips) ? dish?.chips : (dish?.chip ? [dish?.chip] : []);
                      const updated = isActive
                        ? current?.filter((c) => c !== chip?.value)
                        : [...current, chip?.value];
                      onUpdate(dish?.id, 'chips', updated);
                    }}
                    title={isActive ? `Retirer le badge ${chip?.label}` : `Ajouter le badge ${chip?.label}`}
                    className={`px-2 py-0.5 rounded-full text-xs font-medium border transition-smooth ${
                      isActive
                        ? chip?.className
                        : 'bg-transparent border-border text-muted-foreground hover:border-primary hover:text-primary'
                    }`}
                  >
                    {chip?.label}
                  </button>
                );
              })}
            </div>
          </div>
        </td>
        {/* Hidden Toggle */}
        <td className="px-4 py-3">
          <button
            onClick={() => onUpdate(dish?.id, 'hidden', !isHidden)}
            title={isHidden ? 'Afficher le plat' : 'Masquer le plat'}
            className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-smooth border ${
              isHidden
                ? 'bg-muted border-border text-muted-foreground hover:bg-muted/80'
                : 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100'
            }`}
          >
            <Icon name={isHidden ? 'EyeOff' : 'Eye'} size={14} />
            {isHidden ? 'Masqué' : 'Visible'}
          </button>
        </td>
        {/* Delete */}
        <td className="px-4 py-3">
          <button
            onClick={() => onDelete?.(dish?.id)}
            title="Supprimer le plat"
            className="flex items-center justify-center w-8 h-8 rounded-full text-destructive hover:bg-destructive/10 transition-smooth"
          >
            <Icon name="Trash2" size={16} />
          </button>
        </td>
      </tr>
      {/* Dish Scanner Modal */}
      <DishScannerModal
        isOpen={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
        dishName={dish?.name}
        onScanComplete={handleScanComplete}
      />
      {/* GLB Preview Modal */}
      {isGlbPreviewOpen && (
        <tr>
          <td colSpan={10}>
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
              onClick={() => setIsGlbPreviewOpen(false)}
            >
              <div
                className="relative bg-background rounded-xl shadow-2xl p-4 flex flex-col items-center gap-3"
                style={{ width: 480, maxWidth: '95vw' }}
                onClick={(e) => e?.stopPropagation()}
              >
                <div className="flex items-center justify-between w-full">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <Icon name="Box" size={16} color="var(--color-primary)" />
                    Prévisualisation — {dish?.name}
                  </h3>
                  <button
                    onClick={() => setIsGlbPreviewOpen(false)}
                    className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-muted/60 transition-smooth"
                    title="Fermer"
                  >
                    <Icon name="X" size={16} />
                  </button>
                </div>
                <div className="w-full rounded-lg overflow-hidden bg-muted/30" style={{ height: 360 }}>
                  <model-viewer
                    src={dish?.arUrl}
                    alt={`Modèle 3D de ${dish?.name}`}
                    auto-rotate
                    camera-controls
                    style={{ width: '100%', height: '100%', background: 'transparent' }}
                  />
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  Faites glisser pour pivoter · Pincez ou molette pour zoomer
                </p>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default DishEditorRow;